import { FileText, Globe, Info } from 'lucide-react';
import type { DragEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CleanTextInput } from './CleanTextInput';
import { ProgressCard } from './ProgressCard';
import { ResultView } from './ResultView';
import { useCleanTextForm, useWarmUp } from './use-clean-text-form';
import { useCleanTextRun, type RunError } from './use-clean-text-run';

function PageShell({ children }: { readonly children: ReactNode }) {
  const { t } = useTranslation('tools');
  return (
    <div className="space-y-6">
      <div>
        <h2 className="screen-title">{t('cleanText.title')}</h2>
        <p className="screen-summary">{t('cleanText.summary')}</p>
      </div>
      {children}
    </div>
  );
}

function StoppedCard(props: {
  readonly isUrl: boolean;
  readonly name: string;
  readonly onNewDocument: () => void;
}) {
  const { t } = useTranslation('tools');
  const SourceIcon = props.isUrl ? Globe : FileText;
  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center gap-2 text-sm">
        <SourceIcon aria-hidden="true" className="size-4.5" />
        <b>{props.name}</b>
      </div>
      <div role="alert" className="flex gap-3">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-[var(--blue-ink)]" />
        <div className="space-y-2 text-sm">
          <h3 className="text-base font-bold">{t('cleanText.errors.unreadable.title')}</h3>
          <p>{t('cleanText.errors.unreadable.text')}</p>
          <p className="text-muted-foreground">{t('cleanText.errors.unreadable.hint')}</p>
          <Button type="button" variant="outline" onClick={props.onNewDocument}>
            {t('cleanText.errors.unreadable.another')}
          </Button>
        </div>
      </div>
    </section>
  );
}

type Form = ReturnType<typeof useCleanTextForm>;

function InputView({ form, warm }: { readonly form: Form; readonly warm: 'pending' | 'done' }) {
  const { t } = useTranslation('tools');
  const { error } = form;
  const message = (e: RunError): string =>
    t(`cleanText.errors.${e.key}` as 'cleanText.errors.generic', { ...e.values });
  const banner = error?.where === 'banner' ? error : null;
  const buttonKey = form.rerun ? 'rerun' : 'submit';
  return (
    <PageShell>
      <CleanTextInput
        tab={form.tab}
        file={form.file}
        url={form.url}
        fileError={error?.where === 'file' ? message(error) : null}
        urlError={error?.where === 'url' ? message(error) : null}
        banner={banner && { message: message(banner), retry: Boolean(banner.retry) }}
        buttonLabel={
          warm === 'pending' ? t('cleanText.input.starting') : t(`cleanText.input.${buttonKey}`)
        }
        options={form.options}
        onTab={form.onTab}
        onUrl={form.onUrl}
        onChoose={form.choose}
        onSubmit={form.submit}
        onDrop={(event: DragEvent<HTMLElement>) => {
          event.preventDefault();
          form.choose(event.dataTransfer.files[0]);
        }}
      />
    </PageShell>
  );
}

/**
 * «Estrai e oscura» (#557, decision 158; board approved in decision 158): a document or a web
 * address goes to the EU server, which answers the text with names, contacts and codes replaced by
 * placeholders. Nothing is kept. The layout is Strumenti › PDF's.
 */
export function CleanTextPage() {
  const run = useCleanTextRun();
  const warm = useWarmUp();
  const form = useCleanTextForm(run);
  const { error, sourceName } = form;
  const tab = form.tab;

  if (run.phase === 'running') {
    return (
      <PageShell>
        <ProgressCard
          name={sourceName}
          isUrl={tab === 'url'}
          step={run.step}
          onCancel={run.cancel}
        />
      </PageShell>
    );
  }
  if (run.phase === 'result' && run.result) {
    return (
      <PageShell>
        <ResultView
          result={run.result}
          sourceName={sourceName || 'testo'}
          onNewDocument={form.newDocument}
          onAddTerm={form.addTermAndRerun}
          onReprocessLanguage={form.reprocessAs}
        />
      </PageShell>
    );
  }
  if (run.phase === 'stopped' && error) {
    return (
      <PageShell>
        <StoppedCard isUrl={tab === 'url'} name={sourceName} onNewDocument={form.newDocument} />
      </PageShell>
    );
  }

  return <InputView form={form} warm={warm} />;
}
